<script lang="ts">
  import { onMount } from 'svelte';
  import { Button } from '@lib/components';
  import { handleCtaNavigation } from '@lib/utils/navigation';
  import type { LeaderboardBlock } from './types';
  import { getTranslations } from '$lib/interpreter';

  // Browser check - works in both SSR and CSR
  const isBrowser = typeof window !== 'undefined';

  export let block: LeaderboardBlock;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  // Get translations
  let t: ReturnType<typeof getTranslations>['leaderboard'];
  $: {
    if (isBrowser) {
      const surveyData = (window as any).__SURVEY_DATA__;
      const language = surveyData?.config?.language || 'en_us';
      const translations = getTranslations(language);
      t = translations.leaderboard;
    }
  }

  // Leaderboard state
  let leaderboardData: any = null;
  let loading = false;
  let error: string | null = null;
  let currentPage = 1;

  // Width handling
  $: maxWidth = getMaxWidth();

  function getMaxWidth() {
    if (!block.layout?.width) return '800px';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }

  // Get normalized scores from sessionStorage
  function getNormalizedScores(): Record<string, number> {
    if (!isBrowser) return {};
    try {
      const raw = sessionStorage.getItem('walku:weights');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.normalizedScores || {};
    } catch {
      return {};
    }
  }

  // Get surveyId from JWT token, sessionStorage, or window
  function getSurveyId(): string | null {
    if (!isBrowser) return null;
    try {
      // Try extracting from JWT token first (works in fresh windows/incognito)
      const token = getProfileToken();
      if (token) {
        try {
          // Decode JWT payload (base64 decode the middle part)
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            if (payload.surveyId) {
              return payload.surveyId;
            }
          }
        } catch {
          // Failed to decode JWT, try other methods
        }
      }

      // Try sessionStorage (available during survey flow)
      const raw = sessionStorage.getItem('walku:survey');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.surveyId) {
          return parsed.surveyId;
        }
      }

      // Try window.__SURVEY_DATA__ (fallback)
      const surveyData = (window as any).__SURVEY_DATA__;
      if (surveyData?.config?.surveyId) {
        return surveyData.config.surveyId;
      }

      if (surveyData?.surveyId) {
        return surveyData.surveyId;
      }

      return null;
    } catch {
      return null;
    }
  }

  // Get profile token from sessionStorage (JWT for leaderboard access)
  function getProfileToken(): string | null {
    if (!isBrowser) return null;
    try {
      const raw = sessionStorage.getItem('walku:profileToken');
      return raw || null;
    } catch {
      return null;
    }
  }

  // Get API base URL
  function getApiBaseUrl(): string {
    if (!isBrowser) return '';
    // Check for Vite environment variable first
    if (import.meta.env.VITE_FUNCTION_URL) {
      return import.meta.env.VITE_FUNCTION_URL;
    }
    // Fallback to window variable
    return (window as any).__FUNCTION_URL__ || 'http://localhost:3000';
  }

  // Get axis label from survey data
  function getAxisLabel(axisId: string): string {
    if (!isBrowser) return axisId;
    try {
      const surveyData = (window as any).__SURVEY_DATA__;
      if (surveyData && surveyData.axes) {
        const axis = surveyData.axes.find((a: any) => a.id === axisId);
        return axis?.label || axisId;
      }
    } catch {
      return axisId;
    }
    return axisId;
  }

  // Fetch leaderboard data
  async function fetchLeaderboardData(page: number = 1) {
    if (!isBrowser) return;

    const surveyId = getSurveyId();
    const normalizedScores = getNormalizedScores();
    const token = getProfileToken();

    if (!surveyId || !normalizedScores || Object.keys(normalizedScores).length === 0) {
      return;
    }

    if (!token) {
      error = block.errorMessage || 'Authentication token not found. Please complete the survey first.';
      return;
    }

    loading = true;
    error = null;

    try {
      const apiBase = getApiBaseUrl();
      const url = `${apiBase}/profile/leaderboard`;

      const leaderboardConfig = {
        axis: block.axis,
        timeWeight: block.timeWeight ?? 0.3,
        page,
        pageSize: block.pageSize || 10,
        participantField: block.participantField || null,
        scoringMethod: block.scoringMethod || 'pointsPerSecond',
        scoringOptions: block.scoringOptions || {}
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          surveyId,
          normalizedScores,
          leaderboardConfig,
          token
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch leaderboard data: ${response.status}`);
      }

      leaderboardData = await response.json();
      currentPage = page;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load leaderboard';
    } finally {
      loading = false;
    }
  }

  // Pagination handlers
  function goToPage(page: number) {
    fetchLeaderboardData(page);
  }

  function nextPage() {
    if (leaderboardData && currentPage < leaderboardData.leaderboard.totalPages) {
      goToPage(currentPage + 1);
    }
  }

  function prevPage() {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }

  // Fetch on mount
  onMount(() => {
    if (isBrowser) {
      fetchLeaderboardData(1);
    }
  });
</script>

<div class="leaderboard-block" style:--max-width={maxWidth}>
  {#if loading}
    <div class="leaderboard-loading">
      <p>{block.loadingMessage || (t?.loading || 'Loading leaderboard...')}</p>
    </div>
  {:else if error}
    <div class="leaderboard-error">
      <p>{error}</p>
      {#if block.cta && block.cta.label}
        <div class="leaderboard-cta">
          <Button
            label={block.cta.label}
            variant={block.cta.variant || 'primary'}
            size="lg"
            inline={true}
            on:click={() => handleCtaNavigation(block.cta, onCtaClick)}
          />
        </div>
      {/if}
    </div>
  {:else if leaderboardData}
    {#if block.title || block.description}
      <div class="leaderboard-header">
        {#if block.title}
          <h3 class="leaderboard-title">{block.title}</h3>
        {/if}
        {#if block.description}
          <p class="leaderboard-description">{block.description}</p>
        {/if}
      </div>
    {/if}

    <!-- User stats card -->
    {#if (block.showUserStats ?? true) && leaderboardData.userStats}
      <div class="user-stats-card">
        <h4 class="stats-title">{t?.yourPosition || 'Your Position'}</h4>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">{t?.rank || 'Rank'}</span>
            <span class="stat-value">
              {leaderboardData.userStats.rank !== null
                ? `#${leaderboardData.userStats.rank}`
                : 'N/A'}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{t?.percentile || 'Percentile'}</span>
            <span class="stat-value">
              {leaderboardData.userStats.percentile !== null
                ? `${leaderboardData.userStats.percentile}%`
                : 'N/A'}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{getAxisLabel(block.axis)}</span>
            <span class="stat-value">{leaderboardData.userStats.axisScore}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{t?.time || 'Time'} (s)</span>
            <span class="stat-value">
              {leaderboardData.userStats.scoringTime !== null
                ? `${leaderboardData.userStats.scoringTime}s`
                : 'N/A'}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{leaderboardData.scoringMethodLabel || 'Score'}</span>
            <span class="stat-value stat-highlight">{leaderboardData.userStats.performanceScore}</span>
          </div>
        </div>
      </div>
    {/if}

    <!-- Leaderboard table -->
    <div class="leaderboard-table-container">
      <table class="leaderboard-table">
        <thead>
          <tr>
            <th>{t?.rank || 'Rank'}</th>
            <th>{t?.participant || 'Participant'}</th>
            <th>{getAxisLabel(block.axis)}</th>
            <th>{t?.time || 'Time'} (s)</th>
            <th>{leaderboardData.scoringMethodLabel || 'Score'}</th>
          </tr>
        </thead>
        <tbody>
          {#each leaderboardData.leaderboard.entries as entry}
            <tr>
              <td class="rank-cell">#{entry.rank}</td>
              <td class="participant-cell">{entry.nickname || entry.submissionId}</td>
              <td>{entry.axisScore}</td>
              <td>{entry.scoringTime}s</td>
              <td class="score-cell">{entry.performanceScore}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- Pagination controls -->
    {#if leaderboardData.leaderboard.totalPages > 1}
      <div class="pagination">
        <button
          class="pagination-btn"
          on:click={prevPage}
          disabled={currentPage === 1}
        >
          ← {t?.previous || 'Previous'}
        </button>
        <span class="pagination-info">
          {t?.pageOf?.replace('{current}', String(currentPage)).replace('{total}', String(leaderboardData.leaderboard.totalPages)) || `Page ${currentPage} of ${leaderboardData.leaderboard.totalPages}`}
        </span>
        <button
          class="pagination-btn"
          on:click={nextPage}
          disabled={currentPage === leaderboardData.leaderboard.totalPages}
        >
          {t?.next || 'Next'} →
        </button>
      </div>
    {/if}

    <!-- Footer stats -->
    <div class="leaderboard-footer">
      <p class="leaderboard-stats">
        {#if t}
          {(leaderboardData.leaderboard.totalEntries === 1 ? t.basedOnSingle : t.basedOnMultiple).replace('{count}', String(leaderboardData.leaderboard.totalEntries))}
        {:else}
          Based on {leaderboardData.leaderboard.totalEntries.toLocaleString()} {leaderboardData.leaderboard.totalEntries === 1 ? 'participant' : 'participants'}
        {/if}
      </p>
    </div>
  {/if}
</div>

<style>
  .leaderboard-block {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0;
    background: transparent;
    border-radius: 0;
    margin: 0;
    width: 100%;
    max-width: var(--max-width);
    margin-left: auto;
    margin-right: auto;
  }

  .leaderboard-header {
    text-align: center;
    padding: 1rem 1rem 0 1rem;
  }

  .leaderboard-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    color: var(--leaderboard-title-color, #222);
  }

  .leaderboard-description {
    font-size: 1rem;
    color: var(--leaderboard-description-color, #666);
    margin: 0;
  }

  .user-stats-card {
    background: var(--leaderboard-card-bg, #f9fafb);
    border: 1px solid var(--leaderboard-card-border, #e5e7eb);
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin: 0 1rem;
  }

  .stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
    color: var(--leaderboard-text-color, #222);
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .stat-label {
    font-size: 0.875rem;
    color: var(--leaderboard-label-color, #6b7280);
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--leaderboard-value-color, #111827);
  }

  .stat-highlight {
    color: var(--leaderboard-highlight-color, #3b82f6);
  }

  .leaderboard-table-container {
    overflow-x: auto;
    margin: 0 1rem;
    border-radius: 0.5rem;
    border: 1px solid var(--leaderboard-table-border, #e5e7eb);
  }

  .leaderboard-table {
    width: 100%;
    border-collapse: collapse;
    background: var(--leaderboard-table-bg, #fff);
  }

  .leaderboard-table thead {
    background: var(--leaderboard-header-bg, #f9fafb);
    border-bottom: 2px solid var(--leaderboard-table-border, #e5e7eb);
  }

  .leaderboard-table th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--leaderboard-header-color, #374151);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .leaderboard-table td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: var(--leaderboard-cell-color, #1f2937);
    border-bottom: 1px solid var(--leaderboard-row-border, #f3f4f6);
  }

  .leaderboard-table tbody tr:last-child td {
    border-bottom: none;
  }

  .leaderboard-table tbody tr:hover {
    background: var(--leaderboard-row-hover, #f9fafb);
  }

  .rank-cell {
    font-weight: 600;
    color: var(--leaderboard-rank-color, #3b82f6);
  }

  .participant-cell {
    font-family: monospace;
    font-size: 0.8125rem;
    color: var(--leaderboard-participant-color, #6b7280);
  }

  .score-cell {
    font-weight: 600;
  }

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 0 1rem;
  }

  .pagination-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--leaderboard-btn-color, #374151);
    background: var(--leaderboard-btn-bg, #fff);
    border: 1px solid var(--leaderboard-btn-border, #d1d5db);
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pagination-btn:hover:not(:disabled) {
    background: var(--leaderboard-btn-hover-bg, #f9fafb);
    border-color: var(--leaderboard-btn-hover-border, #9ca3af);
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pagination-info {
    font-size: 0.875rem;
    color: var(--leaderboard-pagination-color, #6b7280);
  }

  .leaderboard-footer {
    text-align: center;
    padding: 0 1rem 1rem 1rem;
  }

  .leaderboard-stats {
    font-size: 0.875rem;
    color: var(--leaderboard-stats-color, #6b7280);
    margin: 0;
  }

  .leaderboard-loading,
  .leaderboard-error {
    text-align: center;
    padding: 2rem 1rem;
    color: var(--leaderboard-text-color, #666);
  }

  .leaderboard-cta {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }

  @media (max-width: 640px) {
    .leaderboard-block {
      padding: 0;
      margin: 0;
    }

    .leaderboard-title {
      font-size: 1.25rem;
    }

    .user-stats-card {
      margin: 0 0.5rem;
      padding: 1rem;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }

    .leaderboard-table-container {
      margin: 0 0.5rem;
    }

    .leaderboard-table th,
    .leaderboard-table td {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
    }

    .pagination {
      padding: 0 0.5rem;
    }
  }

  /* Dark mode support */
  :global([data-theme='dark']) .leaderboard-block {
    background: var(--leaderboard-background-dark, #1a1a1a);
  }

  :global([data-theme='dark']) .leaderboard-title {
    color: var(--leaderboard-title-color-dark, #f3f4f6);
  }

  :global([data-theme='dark']) .leaderboard-description,
  :global([data-theme='dark']) .leaderboard-stats {
    color: var(--leaderboard-text-color-dark, #d1d5db);
  }

  :global([data-theme='dark']) .user-stats-card {
    background: var(--leaderboard-card-bg-dark, #1f2937);
    border-color: var(--leaderboard-card-border-dark, #374151);
  }

  :global([data-theme='dark']) .stats-title {
    color: var(--leaderboard-text-color-dark, #f3f4f6);
  }

  :global([data-theme='dark']) .stat-label {
    color: var(--leaderboard-label-color-dark, #9ca3af);
  }

  :global([data-theme='dark']) .stat-value {
    color: var(--leaderboard-value-color-dark, #f9fafb);
  }

  :global([data-theme='dark']) .leaderboard-table {
    background: var(--leaderboard-table-bg-dark, #1f2937);
  }

  :global([data-theme='dark']) .leaderboard-table thead {
    background: var(--leaderboard-header-bg-dark, #111827);
  }

  :global([data-theme='dark']) .leaderboard-table th {
    color: var(--leaderboard-header-color-dark, #d1d5db);
  }

  :global([data-theme='dark']) .leaderboard-table td {
    color: var(--leaderboard-cell-color-dark, #e5e7eb);
    border-bottom-color: var(--leaderboard-row-border-dark, #374151);
  }

  :global([data-theme='dark']) .leaderboard-table tbody tr:hover {
    background: var(--leaderboard-row-hover-dark, #374151);
  }

  :global([data-theme='dark']) .pagination-btn {
    background: var(--leaderboard-btn-bg-dark, #374151);
    border-color: var(--leaderboard-btn-border-dark, #4b5563);
    color: var(--leaderboard-btn-color-dark, #e5e7eb);
  }

  :global([data-theme='dark']) .pagination-btn:hover:not(:disabled) {
    background: var(--leaderboard-btn-hover-bg-dark, #4b5563);
  }
</style>
